import { format } from 'date-fns'

import { formatDate, isDefAndNotEmpty } from 'utils'
import { EventType, Nullable } from 'types'
import { TransformedEvent } from 'stores/ChartStore/types'
import { ReactNode } from 'react'

interface Props {
  days: Date[]
  dateFormat: string
  timeFormat: string
  events: Nullable<Record<string, TransformedEvent[]>>
  eventsMap: Record<EventType, { title: string; color: string }>
  timeToPixels: (date: Date) => number
  margin: number
  dayWidth: number
  gapWidth: number
  cellHeight: number
  onShowTooltip: (e: React.MouseEvent, text: ReactNode) => void
  onHideTooltip: () => void
}

export const LongTermEvents = (props: Props) => {
  const {
    days,
    dateFormat,
    timeFormat,
    events,
    eventsMap,
    timeToPixels,
    margin,
    dayWidth,
    gapWidth,
    cellHeight,
    onShowTooltip,
    onHideTooltip,
  } = props

  return days.map((date, index) => {
    const dayKey = format(date, dateFormat)
    const dayEvents = events?.[dayKey] || []

    // Сортируем: сначала длинные, потом короткие,
    // т.к. если отрисовать короткие под длинными, их не будет видно
    const sortedDayEvents = [...dayEvents].sort((a, b) => {
      const heightA = timeToPixels(b.fragmentEnd) - timeToPixels(b.fragmentStart)
      const heightB = timeToPixels(a.fragmentEnd) - timeToPixels(a.fragmentStart)
      return heightA - heightB
    })

    return (
      isDefAndNotEmpty(sortedDayEvents) &&
      sortedDayEvents.map((event) => {
        const x = margin + index * dayWidth + index * gapWidth
        const y = timeToPixels(event.fragmentStart)

        const startLabel = formatDate(event.fragmentStart, timeFormat)
        const endLabel = formatDate(event.fragmentEnd, timeFormat)
        const eventFromMap = eventsMap[event.eventTypeCode as EventType]
        const tooltipJsx = (
          <>
            <div className="event-name">{eventFromMap?.title}</div>
            {startLabel} <div>—</div> {endLabel}
          </>
        )

        // Если событие длилось несколько секунд, показываем минимальную высоту 2px
        const height = Math.max(timeToPixels(event.fragmentEnd) - y, 2)
        const lessThanHour = height < cellHeight / 2

        if (height < 0) return null

        return (
          <rect
            key={event.id}
            x={x}
            y={y}
            width={dayWidth}
            height={height}
            fill={eventFromMap?.color}
            rx={lessThanHour ? 4 : 10}
            filter="url(#drop-shadow)"
            onMouseEnter={(e) => onShowTooltip(e, tooltipJsx)}
            onMouseLeave={onHideTooltip}
          />
        )
      })
    )
  })
}
